// Pure text/canonicalization helpers for the script edit + validation layer.
// Extracted from the (now-removed) direct-core so the script subsystem is
// self-contained. No I/O, no schema knowledge beyond field names.
import { asList, type Dict, isDict, strOf } from "./shared.js";

// Deep-sort object keys for a stable canonical JSON (idempotency hashing).
export function sortDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((v) => sortDeep(v));
  if (isDict(value)) {
    const sorted: Dict = {};
    for (const k of Object.keys(value).sort()) sorted[k] = sortDeep(value[k]);
    return sorted;
  }
  return value;
}

const _ANNOTATION_RE = /(?:【[^】]*】|[（(][^）)]*[）)])/g;
const _STATE_MIXED_SEPARATORS = ["/", "／", "+", "、", "，", ","];

function reReplace(pattern: RegExp, s: string, replacement: string): string {
  const flags = pattern.flags.includes("g") ? pattern.flags : pattern.flags + "g";
  return s.replace(new RegExp(pattern.source, flags), replacement);
}

function pyStrip(s: string, chars?: string): string {
  if (chars === undefined) return s.trim();
  let start = 0;
  let end = s.length;
  while (start < end && chars.includes(s[start]!)) start += 1;
  while (end > start && chars.includes(s[end - 1]!)) end -= 1;
  return s.slice(start, end);
}

function reSearch(pattern: RegExp, s: string): RegExpExecArray | null {
  const flags = pattern.flags.includes("g") ? pattern.flags.replace("g", "") : pattern.flags;
  return new RegExp(pattern.source, flags).exec(s);
}

// Canonical asset name: strip 【…】/（…） annotations + leading/trailing punctuation.
export function cleanName(name: unknown): string {
  const value = reReplace(_ANNOTATION_RE, strOf(name).trim(), "");
  return pyStrip(value, " \t\r\n:：,，、");
}

// null when the state label is a single clean state; a reason code otherwise.
export function stateLabelError(stateName: unknown): string | null {
  const text = strOf(stateName).trim().replace(/\s+/g, "");
  if (!text) return "empty";
  if (_STATE_MIXED_SEPARATORS.some((sep) => text.includes(sep))) return "mixed_state_label";
  return null;
}

export function stateRejectionReason(targetKind: string, stateName: unknown): string | null {
  const kind = strOf(targetKind).trim();
  if (kind === "actor" || kind === "location" || kind === "prop") return stateLabelError(stateName);
  return strOf(stateName).trim().replace(/\s+/g, "") ? null : "empty";
}

// Heuristic: is a would-be actor name actually a non-actor voice source?
export function inferNonActorSpeakerKind(name: string, scene: Dict | null = null): string {
  const value = strOf(name).trim();
  if (!value) return "actor";
  if (reSearch(/系统|提示音|提示声|机械音|电子音/, value)) return "system";
  if (reSearch(/广播(?!员)|喇叭|电台|播报|通知|警报/, value)) return "broadcast";
  if (reSearch(/众人|众|群体|全体|大家|人群|宾客|观众|群众|围观者/, value)) return "group";
  if (scene) {
    const propSet = new Set(asList<string>(scene["prop_names"]).map((p) => strOf(p).trim()));
    if (propSet.has(value)) return "prop";
    if (value === strOf(scene["location_name"]).trim()) return "location";
  }
  return "actor";
}

// One-line summary of a validation issue list, for command output warnings.
export function summarizeIssues(issues: Dict[]): string[] {
  if (issues.length === 0) return [];
  const counts: Record<string, number> = {};
  for (const item of issues) {
    const sev = strOf(item["severity"]);
    counts[sev] = (counts[sev] ?? 0) + 1;
  }
  const parts = Object.entries(counts).sort(([a], [b]) => a.localeCompare(b)).map(([sev, c]) => `${sev}: ${c}`);
  const first = issues[0]!;
  return [
    parts.join("; "),
    `first: ${first["code"]} - ${first["summary"]}`,
    ...(first["repair_hint"] ? [`repair: ${first["repair_hint"]}`] : []),
  ];
}
